import React from 'react';
import { Users, Calendar, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useCurrentTrials } from '../../hooks/useCurrentTrials';
import { useNavigate } from 'react-router-dom';

export function CurrentTrialsSection() {
  const navigate = useNavigate();
  const { data: trials, isLoading } = useCurrentTrials();

  if (isLoading) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Users className="w-5 h-5 text-rollops-accent" />
            Current Trials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421] mx-auto" />
            <p className="mt-2 text-text-secondary text-sm">Loading trial students...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!trials || trials.length === 0) {
    return (
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <CardTitle className="text-text-primary flex items-center gap-2">
            <Users className="w-5 h-5 text-rollops-accent" />
            Current Trials
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-text-tertiary mx-auto mb-3 opacity-50" />
            <p className="text-text-secondary text-sm">No active trial students at this time</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const getDaysRemaining = (endDate: string | null) => {
    if (!endDate) return null;
    try {
      const end = new Date(endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const diffTime = end.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  return (
    <Card className="bg-[#221F1F] border-[#504A4A]">
      <CardHeader>
        <CardTitle className="text-text-primary flex items-center gap-2">
          <Users className="w-5 h-5 text-rollops-accent" />
          Current Trials ({trials.length})
        </CardTitle>
        <p className="text-text-secondary text-sm mt-1">
          New students on trial subscriptions - provide special attention to convert them to long-term members
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {trials.map(trial => {
            const daysRemaining = getDaysRemaining(trial.contract_end_date);
            const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining >= 0;

            return (
              <div
                key={trial.enrollment_id}
                className="bg-[#363232] rounded-lg p-4 border border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
                onClick={() => {
                  const studentId = trial.child_id || trial.user_id;
                  if (studentId) {
                    navigate(`/students?userId=${studentId}`);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-text-primary font-semibold text-lg mb-1">
                      {trial.student_name}
                    </h3>
                    <p className="text-text-secondary text-sm mb-2">{trial.subscription_name}</p>
                    <div className="flex items-center gap-4 text-xs text-text-tertiary">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>
                          Started: {formatDate(trial.contract_start_date)}
                        </span>
                      </div>
                      {trial.contract_end_date && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span className={isExpiringSoon ? 'text-yellow-400 font-semibold' : ''}>
                            {daysRemaining !== null && daysRemaining >= 0
                              ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
                              : 'Trial ended'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        trial.status === 'active'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                          : trial.status === 'pending_approval'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                      }`}
                    >
                      {trial.status === 'pending_approval' ? 'Pending Approval' : trial.status.charAt(0).toUpperCase() + trial.status.slice(1)}
                    </span>
                  </div>
                </div>

                {trial.classes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#504A4A]">
                    <p className="text-text-secondary text-xs mb-2 font-medium">
                      Enrolled Classes ({trial.classes.length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {trial.classes.map(classEnrollment => (
                        <span
                          key={classEnrollment.class_id}
                          className="px-2 py-1 bg-[#221F1F] border border-[#504A4A] rounded text-xs text-text-secondary"
                        >
                          {classEnrollment.class_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {trial.classes.length === 0 && (
                  <div className="mt-3 pt-3 border-t border-[#504A4A]">
                    <p className="text-text-tertiary text-xs italic">
                      No classes enrolled yet - encourage them to register for classes
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
